import { hideToast, showToast } from "app/actions/toast_actions";
import type { IToast } from "app/components/React/Toast/types";
import type { LeadsType } from "app/pages/sales-intelligence/modules/leads-search/types";
import { dynamicsDomainService } from "app/pages/sales-intelligence/modules/crm-export/utils/exportDomainService";
import { selectWebsitesExportToCrmAs } from "app/pages/sales-intelligence/sub-modules/company-search/store/selectors";
import type { ThunkDependencies } from "single-spa/store/thunk-dependencies";
import type { ThunkDispatchCommon, ThunkGetState } from "single-spa/store/types";

import multiSelectorPanelTrackingService from "../../../../../services/tracking/createMultiSelectorPanelService";
import type { FetchDomainsFn, FetchDomainsParams } from "../../../components/types";
import { exportDomainsToAsync } from "../../../store/actions/export/dynamics";
import { CrmListObjectType } from "../../../types";
import { exportToCrmToastThunk } from "../common";
import { ExportIntegrations } from "app/pages/sales-intelligence/pages/management/types/types";

const exportingDomainsToastDynamicsExportThunk = (toast: IToast) => {
    return (dispatch: ThunkDispatchCommon) => {
        const { getToastComponent } = dynamicsDomainService;

        dispatch(showToast(getToastComponent(toast.children as string), "success", toast.timestamp));
    };
};

const exportDomainsToDynamicsWithNotificationThunk = (
    domains: readonly string[],
    countries: readonly number[],
    leadsType: LeadsType,
    listId: string | undefined,
    toast: IToast,
) => {
    return async (dispatch: ThunkDispatchCommon, getState: ThunkGetState, deps: ThunkDependencies) => {
        const { getErrorToastTitle, getSuccessToastTitle } = dynamicsDomainService;
        const exportToCrmAsState = selectWebsitesExportToCrmAs(getState());

        try {
            const result =
                exportToCrmAsState === CrmListObjectType.Leads
                    ? await deps.si.api.dynamicsExportSettings.exportDomainsAsLeadTo(domains, countries, leadsType)
                    : await deps.si.api.dynamicsExportSettings.exportDomainsTo(
                          domains,
                          countries,
                          leadsType,
                          listId,
                      );
            if (result) {
                const { processedRecords, exportHistoryUrl } = result;
                dispatch(exportDomainsToAsync.success());
                dispatch(hideToast(toast));
                dispatch(
                    exportToCrmToastThunk(getSuccessToastTitle(processedRecords, domains.length), exportHistoryUrl),
                );
                multiSelectorPanelTrackingService.trackMultiSelectorExportToCrm(
                    ExportIntegrations.dynamics,
                    "success",
                    domains.length,
                    processedRecords,
                );
            } else {
                dispatch(hideToast(toast));
                dispatch(exportToCrmToastThunk(getErrorToastTitle()));
                dispatch(exportDomainsToAsync.failure("Couldn't export to Dynamics"));
                multiSelectorPanelTrackingService.trackMultiSelectorExportToCrm(
                    ExportIntegrations.dynamics,
                    "fail",
                    domains.length,
                    0,
                );
            }
        } catch (error) {
            dispatch(hideToast(toast));
            dispatch(exportDomainsToAsync.failure(error));
            dispatch(exportToCrmToastThunk(getErrorToastTitle()));
            multiSelectorPanelTrackingService.trackMultiSelectorExportToCrm(
                ExportIntegrations.dynamics,
                "fail",
                domains.length,
                0,
            );
        }
    };
};

export const createExportTopNDomainsToDynamicsThunk =
    <T extends FetchDomainsParams>(fetchDomainsThunk: FetchDomainsFn<T>) =>
    (params: T) =>
    async (dispatch: ThunkDispatchCommon) => {
        dispatch(exportDomainsToAsync.request());
        const { createToast, getErrorToastTitle, getExportingToastTitle } = dynamicsDomainService;

        const toast = createToast(getExportingToastTitle(params.domainsCount));

        dispatch(exportingDomainsToastDynamicsExportThunk(toast));

        const domains = await fetchDomainsThunk(params);

        if (domains?.length) {
            await dispatch(
                exportDomainsToDynamicsWithNotificationThunk(
                    domains,
                    params.countries,
                    params.leadsType,
                    params.listParams?.id,
                    toast,
                ),
            );
        } else {
            dispatch(hideToast(toast));
            dispatch(exportDomainsToAsync.failure("Couldn't prepare domains to export to Dynamics"));
            dispatch(exportToCrmToastThunk(getErrorToastTitle()));
        }
    };

export const exportDomainsToDynamicsThunk = (
    domains: readonly string[],
    countries: readonly number[],
    leadsType: LeadsType,
    listId?: string,
) => {
    return async (dispatch: ThunkDispatchCommon) => {
        dispatch(exportDomainsToAsync.request());
        const { createToast, getExportingToastTitle } = dynamicsDomainService;

        const toast = createToast(getExportingToastTitle(domains.length));

        dispatch(exportingDomainsToastDynamicsExportThunk(toast));
        await dispatch(exportDomainsToDynamicsWithNotificationThunk(domains, countries, leadsType, listId, toast));
    };
};
